<template>
    <div class="board-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && board.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="board"
                            :validation="$v"
                            :server-errors="serverErrors"
                            @on-name-change="onNameChange"
                        />
                    </div>
                </div>
                <div v-if="editMode && board.id">
                    <h5 class="mb-3 mt-3 col-12 text-uppercase px-1 py-1">
                        POSTS
                    </h5>
                    <div class="card">
                        <div class="card-body">
                            <posts-table :board-id="board.id" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <a
                                :href="previewUrl"
                                class="btn btn-primary btn-static waves-effect waves-light m-1"
                                target="_blank"
                                @click="onPreviewButtonClick"
                            >
                                <i class="far fa-eye" />
                                Preview
                            </a>
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :key="informationKey"
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="board"
                            :slug="board.slug"
                            :options="{ slugBasicUrl: 'boards' }"
                            :slug-error="slugError"
                            :featured-image="board.featuredImageId || null"
                            :campuses="board.campuses"
                            @on-slug-change="onSlugChange"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-featured-image-change="onFeaturedImageChange"
                            @on-campuses-change="onCampusesChange"
                        />
                    </div>
                </div>
            </div>
        </div>

        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { frontendUrl } from '@/config';
import { required } from 'vuelidate/lib/validators';
import PostsTable from '@/components/boards/PostsTable';
import AddEditForm from '@/components/boards/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage board'
    },

    validations: {
        board: {
            name: {
                required
            },
            slug: {
                required(value) {
                    const isRequired = this.editMode;

                    return !!(!isRequired || (isRequired && value));
                }
            },
            formId: {
                required
            },
            isPublic: {
                required
            },
            isBlockedAddPostsByUsers: {
                required
            },
            description: {
                required
            },
            buttonText: {
                required
            }
        }
    },

    components: {
        PostsTable,
        AddEditForm,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit board' : 'Add board',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Boards',
                    to: '/boards'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            board: {
                isPublic: false,
                campuses: []
            },
            serverErrors: [],
            informationKey: 0,
            boardValueStringify: ''
        };
    },

    computed: {
        sections() {
            const sections = {
                permalink: true,
                featuredImage: true,
                campuses: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        options() {
            const options = {};

            return options;
        },

        previewUrl() {
            return `${frontendUrl}/boards/${this.board.slug}`;
        },

        slugError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.board,
                this.serverErrors,
                'slug'
            );
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.board = await this.getBoard(this.id);
                this.boardValueStringify = JSON.stringify(this.board);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }

            return;
        }

        try {
            const { rows } = await this.getCampuses({ perPage: 5 });

            this.board.campuses = rows;
        } catch (error) {
            console.error(error);

            this.$toasterError();
        }
    },

    methods: {
        ...mapActions({
            addBoard: 'boards/store',
            deleteBoard: 'boards/destroy',
            getBoard: 'boards/show',
            updateBoard: 'boards/update',
            getCampuses: 'campuses/index'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const campuses = this.board.campuses.map(campus => campus.id);

            try {
                if (!this.editMode) {
                    const added = await this.addBoard({
                        ...this.board,
                        campuses
                    });

                    this.$router.push(`/boards/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateBoard({
                        id: this.id,
                        data: { ...this.board, campuses }
                    });

                    this.board.updatedAt = updatedAt;
                    this.boardValueStringify = JSON.stringify(this.board);

                    this.$toaster('board updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteBoard(this.id);

                this.$router.push('/boards');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onFeaturedImageChange(imageId) {
            this.board.featuredImageId = imageId;
        },

        onNameChange(name) {
            if (this.editMode) {
                return;
            }

            this.onSlugChange(name);
            this.informationKey++;
        },

        onSlugChange(slug) {
            this.board.slug = slug;
        },

        onDescriptionChange(description) {
            this.board.description = description;
        },

        onIsPublicChange(isPublic) {
            this.board.isPublic = isPublic;
        },

        onPreviewButtonClick(e) {
            if (this.boardValueStringify !== JSON.stringify(this.board)) {
                e.preventDefault();

                this.$toaster('Please save changes before preview!', {
                    title: 'Warning!',
                    variant: 'warning'
                });
            }
        },

        onCampusesChange(campuses) {
            this.board.campuses = campuses;
            this.informationKey++;
        }
    }
};
</script>
